import React, { useEffect, useState, useCallback } from 'react';
import { getAllApplications, updateApplication, createClient } from '../../database/firebase';
import { useUserRole } from '../../context/UserRoleContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEye,
  HiPencil,
  HiTrash,
  HiXMark,
  HiUser,
  HiEnvelope,
  HiCalendarDays,
  HiCalendar,
  HiClock,
  HiDocumentText,
  HiBuildingOffice2,
  HiExclamationTriangle,
  HiCheckCircle,
  HiInformationCircle,
  HiCurrencyDollar,
  HiLightBulb,
  HiPlus,
  HiBars3,
  HiKey,
  HiUserPlus,
  HiDocumentArrowDown,
  HiPhoto,
  HiPlay,
  HiArrowDownTray,
  HiMagnifyingGlass,
} from 'react-icons/hi2';
import { firestore } from '../../database/firebase';
import {
  doc,
  getDoc,
  collection,
  query,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  {
    value: 'in progress',
    label: 'In Progress',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    value: 'completed',
    label: 'Completed',
    color: 'bg-green-100 text-green-700 border-green-200',
  },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200' },
];

const getStatusStyle = status => {
  const option = statusOptions.find(opt => opt.value === status);
  return option ? option.color : 'bg-gray-100 text-gray-600 border-gray-200';
};

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -50, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -50, scale: 0.95 }}
    className={`fixed right-4 top-4 z-50 flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg ${
      type === 'success'
        ? 'border border-green-200 bg-green-50'
        : type === 'error'
          ? 'border border-red-200 bg-red-50'
          : 'border border-gray-200 bg-gray-50'
    }`}
  >
    {type === 'success' && <HiCheckCircle className="h-5 w-5 text-green-600" />}
    {type === 'error' && <HiExclamationTriangle className="h-5 w-5 text-red-600" />}
    {type === 'info' && <HiInformationCircle className="h-5 w-5 text-blue-600" />}
    <span
      className={`text-sm font-medium ${
        type === 'success' ? 'text-green-800' : type === 'error' ? 'text-red-800' : 'text-black'
      }`}
    >
      {message}
    </span>
    <button
      onClick={onClose}
      className={`ml-2 hover:opacity-70 ${
        type === 'success' ? 'text-green-600' : type === 'error' ? 'text-red-600' : 'text-gray-600'
      }`}
    >
      <HiXMark className="h-4 w-4" />
    </button>
  </motion.div>
);

const RejectedApplicationsTable = () => {
  const { userRole } = useUserRole();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [error, setError] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [showHamburgerMenu, setShowHamburgerMenu] = useState({});
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    application: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const canManageApplications = [
    'admin',
    'coach',
    'moderator',
    'Business Advisor',
    'superAdmin',
  ].includes(userRole);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchRejectedApplications = useCallback(async () => {
    setLoading(true);
    try {
      // First try to get from rejected_applications collection
      let rejectedApps = [];
      try {
        const rejectedQuery = query(collection(firestore, 'rejected_applications'));
        const rejectedSnapshot = await getDocs(rejectedQuery);
        rejectedApps = rejectedSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          fromCollection: 'rejected_applications',
        }));
      } catch (err) {}

      // Also get from applications collection where status is rejected
      try {
        const appsQuery = query(
          collection(firestore, 'applications'),
          where('status', '==', 'rejected')
        );
        const appsSnapshot = await getDocs(appsQuery);
        const appsFromCollection = appsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          fromCollection: 'applications',
        }));
        rejectedApps = [...rejectedApps, ...appsFromCollection];
      } catch (err) {
        toast.error('Error fetching rejected applications from applications collection');
      }

      // Remove duplicates based on id
      const uniqueApps = rejectedApps.reduce((acc, app) => {
        if (!acc.find(a => a.id === app.id)) {
          acc.push(app);
        }
        return acc;
      }, []);

      setApplications(uniqueApps);
      if (uniqueApps.length === 0) {
        showToast('No rejected applications found', 'info');
      }
    } catch (err) {
      setError('Failed to fetch rejected applications');
      showToast('Failed to load rejected applications', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Real-time listener
  useEffect(() => {
    const unsubscribeRejected = onSnapshot(
      query(collection(firestore, 'rejected_applications')),
      snapshot => {
        const rejectedApps = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          fromCollection: 'rejected_applications',
        }));
        setApplications(prev => {
          const appsFromApplications = prev.filter(a => a.fromCollection === 'applications');
          const uniqueApps = [...rejectedApps, ...appsFromApplications].reduce((acc, app) => {
            if (!acc.find(a => a.id === app.id)) {
              acc.push(app);
            }
            return acc;
          }, []);
          return uniqueApps;
        });
      },
      err => {
        toast.error('Error listening to rejected applications');
      }
    );

    const unsubscribeApplications = onSnapshot(
      query(collection(firestore, 'applications'), where('status', '==', 'rejected')),
      snapshot => {
        const appsFromCollection = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          fromCollection: 'applications',
        }));
        setApplications(prev => {
          const appsFromRejected = prev.filter(a => a.fromCollection === 'rejected_applications');
          const uniqueApps = [...appsFromRejected, ...appsFromCollection].reduce((acc, app) => {
            if (!acc.find(a => a.id === app.id)) {
              acc.push(app);
            }
            return acc;
          }, []);
          return uniqueApps;
        });
      },
      err => {
        toast.error('Error listening to applications');
      }
    );

    return () => {
      unsubscribeRejected();
      unsubscribeApplications();
    };
  }, []);

  useEffect(() => {
    fetchRejectedApplications();
  }, [fetchRejectedApplications]);

  const handleView = app => {
    setSelectedApplication(app);
    setShowDetailsModal(true);
  };

  const handleEdit = app => {
    setSelectedApp(app);
    setEditStatus(app.status || 'rejected');
    setEditNotes(app.notes || '');
  };

  const handleDeleteClick = app => {
    setDeleteConfirm({
      isOpen: true,
      application: app,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.application) return;

    setIsDeleting(true);
    try {
      if (deleteConfirm.application.fromCollection === 'rejected_applications') {
        await deleteDoc(doc(firestore, 'rejected_applications', deleteConfirm.application.id));
      } else {
        await deleteDoc(doc(firestore, 'applications', deleteConfirm.application.id));
      }
      setApplications(prev => prev.filter(a => a.id !== deleteConfirm.application.id));
      showToast('Rejected application deleted successfully', 'success');
      setDeleteConfirm({ isOpen: false, application: null });
      fetchRejectedApplications();
    } catch (error) {
      showToast(`Failed to delete rejected application: ${error.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, application: null });
  };

  const handleApproveApplication = async app => {
    setApprovalData(app);
    setNewPassword('');
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      showToast('Password must be at least 6 characters long', 'error');
      return;
    }

    if (!approvalData.email && !approvalData.userEmail) {
      showToast('Email address is required for account creation', 'error');
      return;
    }

    const firstName = approvalData.firstname || '';
    const lastName = approvalData.lastname || '';

    if (!firstName || !lastName) {
      showToast(
        'Applicant name is required for account creation. Please ensure the application has first and last name.',
        'error'
      );
      return;
    }

    setSaving(true);
    try {
      const email = approvalData.email || approvalData.userEmail;

      // Create the new user account using a temporary Firebase app (preserves current session)
      const tempApp = initializeApp(
        {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID,
        },
        'temp-app-' + Date.now()
      );

      const tempAuth = getAuth(tempApp);
      const userCredential = await createUserWithEmailAndPassword(tempAuth, email, newPassword);
      const uid = userCredential.user.uid;

      // Prepare client data
      const clientData = {
        userId: uid,
        applicantId: approvalData.applicantId || approvalData.id,
        firstname: firstName,
        lastname: lastName,
        email: email,
        phone: approvalData.phone || '',
        businessStage: approvalData.businessStage || '',
        program: approvalData.program || '',
        location: approvalData.location || '',
        organization: approvalData.organization || '',
        business: approvalData.business || {},
        documents: approvalData.documents || {},
        interview: approvalData.interview || {},
        assigned: approvalData.assigned || '',
        assignedEmail: approvalData.assignedEmail || '',
        assignedCoachName: approvalData.assignedCoachName || '',
        assignedCoachEmail: approvalData.assignedCoachEmail || '',
        role: 'Client',
        status: 'active',
        approvedAt: new Date().toISOString(),
        createdAt: approvalData.createdAt || new Date().toISOString(),
        approvedBy: 'admin',
        restoredFromRejected: true,
        restoredAt: new Date().toISOString(),
      };

      // Perform all operations in parallel
      await Promise.all([
        createClient(clientData, uid),
        // Remove from rejected_applications if it exists there
        approvalData.fromCollection === 'rejected_applications'
          ? deleteDoc(doc(firestore, 'rejected_applications', approvalData.id))
          : deleteDoc(doc(firestore, 'applications', approvalData.id)),
      ]);

      // Sign out from the temporary auth to clean up (does not affect current session)
      await tempAuth.signOut();

      // Update local state
      setApplications(prev => prev.filter(app => app.id !== approvalData.id));

      // Close modal and show success message
      setShowApprovalModal(false);
      showToast(`Account created successfully for ${firstName} ${lastName}!`, 'success');

      // Refresh the applications list
      fetchRejectedApplications();
    } catch (error) {
      let errorMessage = 'Failed to approve application';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email already exists. Please use a different email.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleHamburgerMenu = appId => {
    setShowHamburgerMenu(prev => {
      const newState = {};
      if (!prev[appId]) {
        newState[appId] = true;
      }
      return newState;
    });
  };

  const closeAllHamburgerMenus = () => {
    setShowHamburgerMenu({});
  };

  const handleDocumentPreview = document => {
    setPreviewDocument(document);
    setShowDocumentPreview(true);
  };

  const getFileIcon = fileType => {
    if (fileType?.includes('image')) return <HiPhoto className="h-4 w-4" />;
    if (fileType?.includes('video')) return <HiPlay className="h-4 w-4" />;
    if (fileType?.includes('pdf')) return <HiDocumentText className="h-4 w-4" />;
    return <HiDocumentArrowDown className="h-4 w-4" />;
  };

  const filteredApplications = applications.filter(
    app =>
      searchTerm === '' ||
      app.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.businessStage?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        !event.target.closest('.hamburger-menu-container') &&
        !event.target.closest('.fixed.inset-0.z-40')
      ) {
        closeAllHamburgerMenus();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (!canManageApplications) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <HiExclamationTriangle className="mx-auto mb-4 h-16 w-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">
            You don't have permission to access the rejected applications management system.
          </p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: 'Total Rejected',
      value: applications.length,
      icon: <HiDocumentText className="h-6 w-6 text-red-600" />,
      bg: 'bg-red-100',
    },
    {
      label: 'Can Be Approved',
      value: applications.filter(a => a.email || a.userEmail).length,
      icon: <HiCheckCircle className="h-6 w-6 text-green-600" />,
      bg: 'bg-green-100',
    },
    {
      label: 'With Documents',
      value: applications.filter(a => a.documents && Object.keys(a.documents).length > 0).length,
      icon: <HiDocumentText className="h-6 w-6 text-blue-600" />,
      bg: 'bg-blue-100',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
                Rejected Applications
              </h1>
              <p className="mt-2 text-sm text-gray-600 md:text-base">
                Review and manage all rejected applications. You can approve them to create client
                accounts.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <HiMagnifyingGlass className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search applications..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4"
            >
              <div className="flex items-center gap-2">
                <HiExclamationTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Loading rejected applications...</p>
            </div>
          ) : applications.length === 0 ? (
            <div className="p-8 text-center">
              <HiDocumentText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-black">
                No rejected applications found
              </h3>
              <p className="mt-1 text-gray-600">
                Rejected applications will appear here once they are rejected.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiUser className="h-4 w-4" /> Applicant
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiEnvelope className="h-4 w-4" /> Email
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiBuildingOffice2 className="h-4 w-4" /> Business
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiClock className="h-4 w-4" /> Rejected At
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiDocumentText className="h-4 w-4" /> Documents
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredApplications.map((app, index) => (
                    <motion.tr
                      key={app.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white">
                            {(app.firstname ||
                              app.applicantName ||
                              app.userEmail ||
                              app.id)?.[0]?.toUpperCase() || 'A'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-black">
                              {app.firstname || ''} {app.lastname || ''}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {app.applicantId || app.id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{app.email || app.userEmail}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {app.business?.businessName || app.organization || 'N/A'}
                          </div>
                          <div className="text-gray-500">
                            {app.business?.industry || 'N/A'} •{' '}
                            {app.business?.yearsInOperation || '0'} years
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {app.rejectedAt
                          ? new Date(app.rejectedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : app.createdAt?.toDate
                            ? app.createdAt.toDate().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : app.createdAt || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {app.documents ? (
                            Object.entries(app.documents).map(([docType, doc], docIndex) => (
                              <motion.button
                                key={docIndex}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() =>
                                  handleDocumentPreview({
                                    ...doc,
                                    type: doc.name?.includes('.pdf')
                                      ? 'application/pdf'
                                      : doc.name?.includes('.jpg') || doc.name?.includes('.png')
                                        ? 'image/jpeg'
                                        : 'application/octet-stream',
                                  })
                                }
                                className="flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-100"
                                title={`Preview ${doc.name || docType}`}
                              >
                                {getFileIcon(
                                  doc.name?.includes('.pdf')
                                    ? 'application/pdf'
                                    : doc.name?.includes('.jpg') || doc.name?.includes('.png')
                                      ? 'image/jpeg'
                                      : 'application/octet-stream'
                                )}
                                <span className="max-w-20 truncate">
                                  {docType.replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                              </motion.button>
                            ))
                          ) : (
                            <span className="text-sm text-gray-400">No documents</span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                        <div className="hamburger-menu-container relative">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => toggleHamburgerMenu(app.id)}
                            className="rounded-lg bg-gray-100 p-2 text-gray-600 transition-colors hover:bg-gray-200"
                            title="Actions"
                          >
                            <HiBars3 className="h-4 w-4" />
                          </motion.button>

                          {showHamburgerMenu[app.id] && (
                            <>
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() =>
                                  setShowHamburgerMenu(prev => ({ ...prev, [app.id]: false }))
                                }
                                className="fixed inset-0 z-40 bg-black/20"
                              />

                              <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                className="fixed z-50 w-48 rounded-lg border border-gray-200 bg-white shadow-xl"
                                style={{
                                  position: 'fixed',
                                  top: '100px',
                                  right: '20px',
                                  zIndex: 9999,
                                  maxHeight: '400px',
                                  overflowY: 'auto',
                                }}
                              >
                                <div className="py-1">
                                  <button
                                    onClick={() => {
                                      handleView(app);
                                      setShowHamburgerMenu(prev => ({
                                        ...prev,
                                        [app.id]: false,
                                      }));
                                    }}
                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <HiEye className="h-4 w-4" />
                                    View Details
                                  </button>

                                  {(app.email || app.userEmail) && (
                                    <button
                                      onClick={() => {
                                        handleApproveApplication(app);
                                        setShowHamburgerMenu(prev => ({
                                          ...prev,
                                          [app.id]: false,
                                        }));
                                      }}
                                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-green-700 hover:bg-green-50"
                                    >
                                      <HiCheckCircle className="h-4 w-4" />
                                      Approve & Create Account
                                    </button>
                                  )}

                                  <button
                                    onClick={() => {
                                      handleEdit(app);
                                      setShowHamburgerMenu(prev => ({
                                        ...prev,
                                        [app.id]: false,
                                      }));
                                    }}
                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-orange-700 hover:bg-orange-50"
                                  >
                                    <HiPencil className="h-4 w-4" />
                                    Edit Application
                                  </button>

                                  <button
                                    onClick={() => {
                                      handleDeleteClick(app);
                                      setShowHamburgerMenu(prev => ({
                                        ...prev,
                                        [app.id]: false,
                                      }));
                                    }}
                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                                  >
                                    <HiTrash className="h-4 w-4" />
                                    Delete Application
                                  </button>
                                </div>
                              </motion.div>
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {stats.map((stat, idx) => (
            <div
              key={stat.label}
              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-black">{stat.value}</p>
                </div>
                <div className={`rounded-full p-3 ${stat.bg}`}>{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>

        <AnimatePresence>
          {showApprovalModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
              >
                <div className="mb-6">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white">
                      <HiUserPlus className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Approve Application</h3>
                      <p className="text-sm text-gray-600">
                        Create account for {approvalData.firstname} {approvalData.lastname}
                      </p>
                      <p className="text-xs text-gray-500">
                        Business:{' '}
                        {approvalData.business?.businessName || approvalData.organization || 'N/A'}{' '}
                        • {approvalData.business?.industry || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-lg bg-blue-50 p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <HiKey className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Set Account Password</span>
                    </div>
                    <p className="mb-4 text-sm text-blue-700">
                      This password will be used by the user to login to their account.
                    </p>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <HiKey className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Enter secure password (min 6 characters)"
                        minLength={6}
                      />
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Password requirements: Minimum 6 characters
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowApprovalModal(false)}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-700 transition-colors hover:bg-gray-50"
                    disabled={saving}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleApprovalSubmit}
                    disabled={saving || !newPassword || newPassword.length < 6}
                    className="flex-1 rounded-lg bg-gradient-to-r from-green-600 to-green-700 px-4 py-3 text-white shadow-lg transition-all hover:shadow-xl disabled:opacity-50 disabled:hover:scale-100"
                  >
                    <div className="flex items-center justify-center gap-2">
                      {saving ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          <span>Creating Account...</span>
                        </>
                      ) : (
                        <>
                          <HiCheckCircle className="h-5 w-5" />
                          <span>Approve & Create Account</span>
                        </>
                      )}
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showDocumentPreview && previewDocument && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl"
              >
                <div className="flex items-center justify-between border-b border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    {getFileIcon(previewDocument.type)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {previewDocument.name || 'Document Preview'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {previewDocument.type || 'Unknown file type'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => window.open(previewDocument.url, '_blank')}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                      <HiArrowDownTray className="h-4 w-4" />
                      Download
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowDocumentPreview(false)}
                      className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
                    >
                      <HiXMark className="h-5 w-5" />
                    </motion.button>
                  </div>
                </div>
                <div className="p-6">
                  {previewDocument.type?.includes('image') ? (
                    <img
                      src={previewDocument.url}
                      alt={previewDocument.name}
                      className="mx-auto max-h-96 rounded-lg object-contain"
                    />
                  ) : previewDocument.type?.includes('pdf') ? (
                    <iframe
                      src={previewDocument.url}
                      className="h-96 w-full rounded-lg border"
                      title={previewDocument.name}
                    />
                  ) : (
                    <div className="flex h-96 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                      <div className="text-center">
                        <HiDocumentText className="mx-auto h-16 w-16 text-gray-400" />
                        <p className="mt-2 text-gray-600">
                          Preview not available for this file type
                        </p>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => window.open(previewDocument.url, '_blank')}
                          className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                        >
                          <HiArrowDownTray className="h-4 w-4" />
                          Download to view
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showDetailsModal && selectedApplication && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
              >
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white">
                      <HiUser className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Rejected Application Details
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedApplication.firstname} {selectedApplication.lastname}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowDetailsModal(false)}
                    className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
                  >
                    <HiXMark className="h-5 w-5" />
                  </motion.button>
                </div>

                <div className="space-y-6 p-6">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <HiUser className="h-5 w-5" />
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Full Name</label>
                        <p className="text-gray-900">
                          {selectedApplication.firstname} {selectedApplication.lastname}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Email</label>
                        <p className="text-gray-900">
                          {selectedApplication.email || selectedApplication.userEmail}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Phone</label>
                        <p className="text-gray-900">{selectedApplication.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Application ID</label>
                        <p className="text-gray-900">
                          {selectedApplication.applicantId || selectedApplication.id}
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedApplication.business && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <HiBuildingOffice2 className="h-5 w-5" />
                        Business Information
                      </h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Business Name</label>
                          <p className="text-gray-900">
                            {selectedApplication.business.businessName || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Industry</label>
                          <p className="text-gray-900">
                            {selectedApplication.business.industry || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Registration Number
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.business.registrationNumber || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Years in Operation
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.business.yearsInOperation || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedApplication.documents && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <HiDocumentText className="h-5 w-5" />
                        Documents
                      </h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {Object.entries(selectedApplication.documents).map(([docType, doc]) => (
                          <div
                            key={docType}
                            className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
                          >
                            <div className="flex items-center gap-3">
                              {getFileIcon(
                                doc.name?.includes('.pdf')
                                  ? 'application/pdf'
                                  : doc.name?.includes('.jpg') || doc.name?.includes('.png')
                                    ? 'image/jpeg'
                                    : 'application/octet-stream'
                              )}
                              <div>
                                <p className="font-medium text-gray-900">
                                  {docType.replace(/([A-Z])/g, ' $1').trim()}
                                </p>
                                <p className="text-sm text-gray-500">{doc.name}</p>
                              </div>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() =>
                                handleDocumentPreview({
                                  ...doc,
                                  type: doc.name?.includes('.pdf')
                                    ? 'application/pdf'
                                    : doc.name?.includes('.jpg') || doc.name?.includes('.png')
                                      ? 'image/jpeg'
                                      : 'application/octet-stream',
                                })
                              }
                              className="rounded-lg bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
                            >
                              View
                            </motion.button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <HiExclamationTriangle className="h-5 w-5 text-red-600" />
                      Rejection Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Status</label>
                        <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                          Rejected
                        </span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Rejected At</label>
                        <p className="text-gray-900">
                          {selectedApplication.rejectedAt
                            ? new Date(selectedApplication.rejectedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Rejected By</label>
                        <p className="text-gray-900">{selectedApplication.rejectedBy || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Permanently Delete Application"
          message="Are you sure you want to permanently delete this rejected application? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default RejectedApplicationsTable;
